import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Alert,
    CircularProgress,
    Card,
    CardContent,
    Grid,
    Chip,
    LinearProgress,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider,
} from '@mui/material';
import {
    TrendingUp,
    Timeline,
    Assessment,
    Mic,
    Speed,
    VolumeUp,
    School,
    Star,
    CalendarToday,
} from '@mui/icons-material';
import { useReading } from '@hooks/useReading';
import type { ReadingAnalytics } from '@services/reading/types';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`analytics-tabpanel-${index}`}
            aria-labelledby={`analytics-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
        </div>
    );
};

const EnhancedAnalytics: React.FC = () => {
    const { readingState } = useReading();
    const [analytics, setAnalytics] = useState<ReadingAnalytics | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [currentTab, setCurrentTab] = useState(0);
    const [timeRange, setTimeRange] = useState(30);

    useEffect(() => {
        loadAnalytics();
    }, [timeRange]);

    const loadAnalytics = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const { default: readingService } = await import('@services/reading/readingService');
            const analyticsData = await readingService.getAnalytics(timeRange);
            setAnalytics(analyticsData);
        } catch (err: any) {
            const errorMessage = err?.response?.data?.message || err?.message || 'Failed to load analytics data';
            setError(errorMessage);
            console.error('Analytics error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setCurrentTab(newValue);
    };

    const getScoreColor = (score: number) => {
        if (score >= 80) return 'success';
        if (score >= 60) return 'warning';
        return 'error';
    };

    const getTrendIcon = (trend: string) => {
        switch (trend) {
            case 'improving':
                return '📈';
            case 'declining':
                return '📉';
            default:
                return '➡️';
        }
    };

    if (isLoading) {
        return (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ mt: 1 }}>
                    Loading detailed analytics...
                </Typography>
            </Paper>
        );
    }

    if (error) {
        return (
            <Paper sx={{ p: 3 }}>
                <Alert severity="error">{error}</Alert>
                <Button onClick={loadAnalytics} sx={{ mt: 2 }}>
                    Retry
                </Button>
            </Paper>
        );
    }

    return (
        <Box>
            {/* Header with Time Range Selector */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Typography variant="h5" display="flex" alignItems="center" gap={1}>
                        <Assessment />
                        Enhanced Reading Analytics
                    </Typography>

                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Time Range</InputLabel>
                        <Select
                            value={timeRange}
                            onChange={(e) => setTimeRange(e.target.value as number)}
                            label="Time Range"
                        >
                            <MenuItem value={7}>Last 7 days</MenuItem>
                            <MenuItem value={30}>Last 30 days</MenuItem>
                            <MenuItem value={90}>Last 3 months</MenuItem>
                            <MenuItem value={180}>Last 6 months</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                {/* Key Performance Indicators */}
                {analytics && (
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <Typography variant="h4" color="primary.main" fontWeight="bold">
                                        {analytics.total_sessions}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        gap={1}
                                    >
                                        <School />
                                        Total Sessions
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <Typography variant="h4" color="info.main" fontWeight="bold">
                                        {analytics.total_reading_time
                                            ? Math.round(analytics.total_reading_time / 60)
                                            : 0}
                                        m
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        gap={1}
                                    >
                                        <CalendarToday />
                                        Reading Time
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <Typography variant="h4" color="success.main" fontWeight="bold">
                                        {analytics.average_pronunciation_score
                                            ? Math.round(analytics.average_pronunciation_score)
                                            : 0}
                                        %
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        gap={1}
                                    >
                                        <Mic />
                                        Avg. Pronunciation
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <Typography variant="h4" color="warning.main" fontWeight="bold">
                                        {analytics.average_comprehension_score
                                            ? Math.round(analytics.average_comprehension_score)
                                            : 0}
                                        %
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        gap={1}
                                    >
                                        <Star />
                                        Avg. Comprehension
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}
            </Paper>

            {/* Detailed Analytics Tabs */}
            <Paper sx={{ width: '100%' }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={currentTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
                        <Tab label="Progress Trends" icon={<TrendingUp />} iconPosition="start" />
                        <Tab label="Skill Breakdown" icon={<Assessment />} iconPosition="start" />
                        <Tab label="Performance Timeline" icon={<Timeline />} iconPosition="start" />
                        <Tab label="Recent Achievements" icon={<Star />} iconPosition="start" />
                    </Tabs>
                </Box>

                {/* Progress Trends Tab */}
                <TabPanel value={currentTab} index={0}>
                    {analytics && (
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                Weekly Progress Overview
                            </Typography>

                            <Grid container spacing={3} mb={3}>
                                <Grid item xs={12} md={6}>
                                    <Card>
                                        <CardContent>
                                            <Typography
                                                variant="h6"
                                                gutterBottom
                                                display="flex"
                                                alignItems="center"
                                                gap={1}
                                            >
                                                {getTrendIcon(analytics.improvement_trend)}
                                                Overall Trend: {analytics.improvement_trend}
                                            </Typography>
                                            <LinearProgress
                                                variant="determinate"
                                                value={85}
                                                sx={{ height: 8, borderRadius: 4, mb: 2 }}
                                            />
                                            <Typography variant="body2" color="text.secondary">
                                                Your reading skills have been {analytics.improvement_trend} over the
                                                past {timeRange} days.
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Card>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>
                                                Reading Speed Progress
                                            </Typography>
                                            <Typography variant="h4" color="primary.main" fontWeight="bold">
                                                {analytics.average_reading_speed
                                                    ? Math.round(analytics.average_reading_speed)
                                                    : 0}{' '}
                                                WPM
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Average words per minute
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>

                            {/* Weekly Progress Chart */}
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Weekly Progress Chart
                                    </Typography>
                                    {analytics.weekly_progress && analytics.weekly_progress.length > 0 ? (
                                        <Box>
                                            {analytics.weekly_progress.map((week, index) => (
                                                <Box key={index} mb={2}>
                                                    <Box display="flex" justifyContent="space-between" mb={1}>
                                                        <Typography variant="body2">Week {index + 1}</Typography>
                                                        <Typography variant="body2" fontWeight="bold">
                                                            {Math.round(week.average_score)}%
                                                        </Typography>
                                                    </Box>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={week.average_score}
                                                        color={getScoreColor(week.average_score)}
                                                        sx={{ height: 6, borderRadius: 3 }}
                                                    />
                                                </Box>
                                            ))}
                                        </Box>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            Start practicing to see your progress trends!
                                        </Typography>
                                    )}
                                </CardContent>
                            </Card>
                        </Box>
                    )}
                </TabPanel>

                {/* Skill Breakdown Tab */}
                <TabPanel value={currentTab} index={1}>
                    {analytics && analytics.skill_breakdown && (
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                Detailed Skill Analysis
                            </Typography>

                            <Grid container spacing={3}>
                                {Object.entries(analytics.skill_breakdown).map(([skill, data]: [string, any]) => (
                                    <Grid item xs={12} md={6} key={skill}>
                                        <Card>
                                            <CardContent>
                                                <Typography
                                                    variant="h6"
                                                    gutterBottom
                                                    sx={{ textTransform: 'capitalize' }}
                                                >
                                                    {skill.replace('_', ' ')}
                                                </Typography>

                                                <Box mb={2}>
                                                    <Box display="flex" justifyContent="space-between" mb={1}>
                                                        <Typography variant="body2">Current Level</Typography>
                                                        <Typography variant="body2" fontWeight="bold">
                                                            {Math.round(data.current_score)}%
                                                        </Typography>
                                                    </Box>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={data.current_score}
                                                        color={getScoreColor(data.current_score)}
                                                        sx={{ height: 8, borderRadius: 4 }}
                                                    />
                                                </Box>

                                                <Divider sx={{ my: 2 }} />

                                                <Box>
                                                    <Typography variant="subtitle2" gutterBottom>
                                                        Improvement Areas:
                                                    </Typography>
                                                    {data.improvement_areas &&
                                                        data.improvement_areas.map((area: string, index: number) => (
                                                            <Chip
                                                                key={index}
                                                                label={area}
                                                                size="small"
                                                                color="info"
                                                                sx={{ mr: 1, mb: 1 }}
                                                            />
                                                        ))}
                                                </Box>

                                                <Box mt={2}>
                                                    <Typography variant="subtitle2" gutterBottom>
                                                        Recent Trend: {data.trend || 'stable'}
                                                    </Typography>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    )}
                </TabPanel>

                {/* Performance Timeline Tab */}
                <TabPanel value={currentTab} index={2}>
                    {analytics && (
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                Performance Timeline
                            </Typography>

                            <TableContainer component={Paper}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Sessions</TableCell>
                                            <TableCell>Reading Time</TableCell>
                                            <TableCell>Avg Score</TableCell>
                                            <TableCell>Improvement</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {analytics.weekly_progress &&
                                            analytics.weekly_progress.map((week, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>Week {index + 1}</TableCell>
                                                    <TableCell>{week.sessions || 0}</TableCell>
                                                    <TableCell>{Math.round((week.reading_time || 0) / 60)}m</TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={`${Math.round(week.average_score)}%`}
                                                            color={getScoreColor(week.average_score)}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        {index > 0 && (
                                                            <Box display="flex" alignItems="center" gap={1}>
                                                                {week.average_score >
                                                                analytics.weekly_progress[index - 1].average_score
                                                                    ? '📈'
                                                                    : '📉'}
                                                                {Math.abs(
                                                                    week.average_score -
                                                                        analytics.weekly_progress[index - 1]
                                                                            .average_score,
                                                                ).toFixed(1)}
                                                                %
                                                            </Box>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}
                </TabPanel>

                {/* Recent Achievements Tab */}
                <TabPanel value={currentTab} index={3}>
                    {analytics && analytics.recent_achievements && (
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                Recent Achievements & Milestones
                            </Typography>

                            <Grid container spacing={3}>
                                {analytics.recent_achievements.map((achievement, index) => (
                                    <Grid item xs={12} md={6} key={index}>
                                        <Card sx={{ border: '2px solid', borderColor: 'success.light' }}>
                                            <CardContent>
                                                <Box display="flex" alignItems="center" gap={2} mb={2}>
                                                    <Typography variant="h4">
                                                        {achievement.badge_icon || '🏆'}
                                                    </Typography>
                                                    <Box>
                                                        <Typography variant="h6">{achievement.title}</Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {achievement.description}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                <Chip
                                                    label={`Earned on ${achievement.earned_at}`}
                                                    color="success"
                                                    size="small"
                                                />
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}

                                {(!analytics.recent_achievements || analytics.recent_achievements.length === 0) && (
                                    <Grid item xs={12}>
                                        <Card>
                                            <CardContent sx={{ textAlign: 'center', py: 6 }}>
                                                <Typography variant="h4" sx={{ mb: 2 }}>
                                                    🎯
                                                </Typography>
                                                <Typography variant="h6" gutterBottom>
                                                    Keep Practicing!
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Complete more reading sessions to unlock achievements and
                                                    milestones.
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    )}
                </TabPanel>
            </Paper>
        </Box>
    );
};

export default EnhancedAnalytics;
